import { useState, useEffect } from 'react';
import { TableRow, TableCell, TextField, Button, Box, Typography, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import StandardTable from '../ui/StandardTable';
import TooltipIconButton from '../ui/TooltipIconButton';

const fmt = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v ?? 0);

const typeLabels = { 1: 'Adicional', 2: 'Desconto', 3: 'Taxa' };

// Subcomponente para editar grossValue com estados de edição/visualização
function GrossValueField({ value, onChange }) {
  const [isEditing, setIsEditing] = useState(value === null);
  const [inputValue, setInputValue] = useState(value?.toString() ?? '');

  useEffect(() => {
    if (value != null) {
      setIsEditing(false);
      setInputValue(value.toString());
    }
  }, [value]);

  const handleCommit = () => {
    const num = inputValue ? parseFloat(inputValue) : null;
    if (num !== null && num > 0) {
      onChange(num);
      setIsEditing(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleCommit();
    if (e.key === 'Escape') {
      setInputValue(value?.toString() ?? '');
      setIsEditing(false);
    }
  };

  if (!isEditing) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" fontWeight={600}>{fmt(value)}</Typography>
        <IconButton
          size="small"
          onClick={() => {
            setIsEditing(true);
            setInputValue(value?.toString() ?? '');
          }}
          sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  }

  return (
    <TextField
      type="number"
      size="small"
      autoFocus
      placeholder="Informe o valor"
      value={inputValue}
      onChange={(e) => setInputValue(e.target.value)}
      onBlur={handleCommit}
      onKeyDown={handleKeyDown}
      inputProps={{ step: '0.01', min: '0' }}
      sx={{ width: 150 }}
    />
  );
}

export default function FinanceItemsTable({
  grossValue, onGrossChange, items, onItemsChange, onDeleteItem, onAddClick, serviceName
}) {
  const calculateSubtotal = (index) => {
    if (grossValue === null) return null;
    let subtotal = grossValue;
    for (let i = 0; i < items.length && i <= index; i++) {
      const item = items[i];
      const delta = item.valueType === 2 ? (subtotal * (item.value / 100)) : item.value;
      const sign = item.type === 1 ? 1 : -1;
      subtotal += delta * sign;
    }
    return Math.round(subtotal * 100) / 100;
  };

  const finalNetValue = items.length > 0 ? calculateSubtotal(items.length - 1) : grossValue;

  const columns = [
    { id: 'description', label: 'Descrição' },
    { id: 'additional', label: 'Adicional' },
    { id: 'discount', label: 'Desconto' },
    { id: 'fee', label: 'Taxa' },
    { id: 'subtotal', label: 'Subtotal', align: 'right' },
    { id: 'actions', label: 'Ações', align: 'center', width: 60 },
  ];

  return (
    <>
      <StandardTable columns={columns} isEmpty={false}>
        {/* Linha do serviço — sempre visível */}
        <TableRow>
          <TableCell>
            <Typography variant="body2" fontWeight={600}>
              {serviceName || 'Serviço'}
            </Typography>
          </TableCell>
          <TableCell>—</TableCell>
          <TableCell>—</TableCell>
          <TableCell>—</TableCell>
          <TableCell align="right">
            <GrossValueField value={grossValue} onChange={onGrossChange} />
          </TableCell>
          <TableCell />
        </TableRow>

        {grossValue != null && (
          <>

            {items.map((item, idx) => {
              const subtotal = calculateSubtotal(idx);
              const showVal = item.valueType === 2 ? `${item.value}%` : fmt(item.value);
              const colType = item.type === 1 ? 'additional' : item.type === 2 ? 'discount' : 'fee';

              return (
                <TableRow key={idx}>
                  <TableCell>{item.description}</TableCell>
                  <TableCell>{colType === 'additional' ? showVal : '—'}</TableCell>
                  <TableCell>{colType === 'discount' ? showVal : '—'}</TableCell>
                  <TableCell>{colType === 'fee' ? showVal : '—'}</TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{fmt(subtotal)}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <TooltipIconButton
                      title="Remover"
                      icon={<DeleteIcon />}
                      onClick={() => onDeleteItem(idx)}
                    />
                  </TableCell>
                </TableRow>
              );
            })}

            <TableRow sx={{ backgroundColor: 'action.hover' }}>
              <TableCell colSpan={6}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between' }}>
                  <div>
                    <Typography variant="caption" color="text.secondary">BRUTO</Typography>
                    <Typography variant="h6">{fmt(grossValue)}</Typography>
                  </div>
                  <div>
                    <Typography variant="caption" color="text.secondary">LÍQUIDO</Typography>
                    <Typography variant="h6" color="primary.main">{fmt(finalNetValue)}</Typography>
                  </div>
                </Box>
              </TableCell>
            </TableRow>
          </>
        )}
      </StandardTable>

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={onAddClick}
          disabled={grossValue == null}
        >
          Adicionar item
        </Button>
      </Box>
    </>
  );
}
